import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ResetPassTpxMetrics } from '../../../../utilities/BL/Xtp';

const MASTER_PASSWORD_REQUIRED = 'evitarborrarregistros456852';

/**
 * MODAL: XtpResetPassTpxModal
 * Zona de Riesgo - Reinicio Exclusivo de Estadísticas del Portal de Pases (/passtpx).
 * Protege contra reinicios accidentales validando la clave maestra 'evitarborrarregistros456852'.
 * Garantiza que NO se borren participantes ni ganadores.
 */
export const XtpResetPassTpxModal = ({
    isOpen = false,
    onClose = () => {},
    onResetSuccess = () => {},
    showMessage = () => {}
}) => {
    const [passwordInput, setPasswordInput] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        if (isOpen) {
            setPasswordInput('');
            setErrorMessage('');
            setIsSubmitting(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleConfirm = async (e) => {
        if (e) e.preventDefault();

        if (passwordInput.trim() !== MASTER_PASSWORD_REQUIRED) {
            setErrorMessage('Contraseña incorrecta. No tienes autorización para reiniciar las estadísticas.');
            return;
        }

        setIsSubmitting(true);
        setErrorMessage('');

        try {
            const res = await ResetPassTpxMetrics(passwordInput.trim(), 'Staff Admin');
            if (res && res.success) {
                showMessage({
                    type: 'success',
                    message: res.message || 'Las estadísticas de /passtpx han sido reiniciadas exitosamente.'
                });
                onResetSuccess();
                onClose();
            } else {
                setErrorMessage(res?.message || 'Error al ejecutar el reinicio de estadísticas en el servidor.');
            }
        } catch (error) {
            console.error('Error en XtpResetPassTpxModal:', error);
            setErrorMessage('Error de conexión con el servidor al procesar el reinicio.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div
            className="modal fade show d-block"
            tabIndex="-1"
            role="dialog"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 1060 }}
        >
            <div className="modal-dialog modal-dialog-centered" role="document">
                <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                    {/* Header */}
                    <div className="modal-header bg-danger text-white py-3 px-4 border-0">
                        <h5 className="modal-title fw-bold d-flex align-items-center gap-2 mb-0">
                            <i className="bi bi-shield-exclamation fs-4"></i>
                            Reiniciar Estadísticas de /passtpx
                        </h5>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            aria-label="Cerrar"
                            onClick={onClose}
                            disabled={isSubmitting}
                        />
                    </div>

                    {/* Body */}
                    <div className="modal-body p-4 bg-white text-dark">
                        {errorMessage && (
                            <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" role="alert">
                                <i className="bi bi-exclamation-triangle-fill fs-5"></i>
                                <span className="small fw-semibold">{errorMessage}</span>
                            </div>
                        )}

                        <div className="p-3 bg-danger-subtle rounded-3 border border-danger border-opacity-25 mb-3">
                            <div className="d-flex align-items-start gap-2">
                                <i className="bi bi-info-circle-fill text-danger fs-4 flex-shrink-0 mt-0.5"></i>
                                <div>
                                    <h6 className="text-danger fw-bold mb-1">Reinicio Exclusivo de Métricas y Búsquedas</h6>
                                    <p className="small text-danger-emphasis mb-0">
                                        Esta acción restablecerá a <strong>CERO</strong> todas las métricas de consultas, búsquedas,
                                        escaneos QR y descargas del portal <strong>/passtpx</strong>.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="p-3 bg-success-subtle rounded-3 border border-success border-opacity-25 mb-3">
                            <div className="d-flex align-items-center gap-2 text-success-emphasis small fw-semibold">
                                <i className="bi bi-check-shield-fill text-success fs-5 flex-shrink-0"></i>
                                <span>Los aficionados registrados y los boletos ganadores NO serán eliminados ni modificados.</span>
                            </div>
                        </div>

                        <form onSubmit={handleConfirm}>
                            <div className="mb-3">
                                <label className="form-label fw-bold small text-secondary">
                                    Contraseña Maestra de Seguridad:
                                </label>
                                <div className="input-group">
                                    <span className="input-group-text bg-light border-end-0">
                                        <i className="bi bi-key-fill text-danger"></i>
                                    </span>
                                    <input
                                        type="password"
                                        className="form-control border-start-0"
                                        placeholder="Ingresa la contraseña para autorizar"
                                        value={passwordInput}
                                        onChange={(e) => setPasswordInput(e.target.value)}
                                        disabled={isSubmitting}
                                        autoFocus
                                    />
                                </div>
                                <div className="form-text small text-muted">
                                    Se requiere autorización para reiniciar los contadores de telemetría.
                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-2 pt-2">
                                <button
                                    type="button"
                                    className="btn btn-light fw-semibold px-3"
                                    onClick={onClose}
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-danger fw-bold d-flex align-items-center gap-2 px-3"
                                    disabled={isSubmitting || !passwordInput.trim()}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                            <span>Reiniciando...</span>
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-arrow-counterclockwise"></i>
                                            <span>Reiniciar Estadísticas</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

XtpResetPassTpxModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onResetSuccess: PropTypes.func.isRequired,
    showMessage: PropTypes.func.isRequired
};
